import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';
import Avatar from '../components/Avatar';
import BadgePreviewModal from '../components/BadgePreviewModal';

const statusLabels = {
  draft: 'مسودة', submitted: 'مُرسل', under_review: 'قيد المراجعة',
  approved: 'معتمد', rejected: 'مرفوض', printed: 'مطبوع'
};

const fields = [
  ['full_name_ar', 'الاسم الكامل (عربي)', 'text'],
  ['full_name_en', 'الاسم الكامل (English)', 'text'],
  ['gender', 'الجنس', 'gender'],
  ['date_of_birth', 'تاريخ الميلاد', 'date'],
  ['nationality', 'الجنسية', 'text'],
  ['national_id', 'رقم الهوية الوطنية', 'text'],
  ['passport_no', 'رقم جواز السفر', 'text'],
  ['passport_expiry', 'تاريخ انتهاء الجواز', 'date'],
  ['job_title', 'الصفة الوظيفية', 'text'],
  ['email', 'البريد الإلكتروني', 'email'],
  ['phone', 'رقم الهاتف', 'text'],
  ['emergency_contact_name', 'جهة اتصال الطوارئ', 'text'],
  ['emergency_contact_phone', 'هاتف الطوارئ', 'text'],
];

export default function ParticipantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user.role === 'admin' || user.role === 'organization';
  const canReview = user.role === 'admin' || user.role === 'reviewer';
  const canPreviewBadge = user.role === 'admin' || user.role === 'reviewer';

  const [p, setP] = useState(null);
  const [results, setResults] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [photo, setPhoto] = useState(null);
  const [msg, setMsg] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [showBadgePreview, setShowBadgePreview] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const quickPhotoRef = useRef(null);

  const load = async () => {
    const { data } = await client.get(`/participants/${id}`);
    setP(data);
    setForm(data);
    try {
      const r = await client.get(`/results/participant/${id}`);
      setResults(r.data);
    } catch { setResults([]); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const save = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fields.forEach(([key]) => { if (form[key] !== undefined && form[key] !== null) fd.append(key, form[key]); });
    if (photo) fd.append('photo', photo);
    await client.put(`/participants/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    setMsg('تم حفظ التعديلات');
    setEditing(false);
    setPhoto(null);
    load();
    setTimeout(() => setMsg(''), 2500);
  };

  const quickPhotoUpload = async (file) => {
    if (!file || !canEdit) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      await client.put(`/participants/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMsg('تم تحديث الصورة الشخصية');
      load();
      setTimeout(() => setMsg(''), 2500);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const changeStatus = async (status, reason) => {
    await client.patch(`/participants/${id}/status`, { status, rejection_reason: reason });
    setShowReject(false);
    setRejectReason('');
    load();
  };

  const downloadBadge = () => {
    window.open(`${client.defaults.baseURL}/badges/${id}/pdf?token=${localStorage.getItem('accred_token')}&_t=${Date.now()}`, '_blank');
  };

  const exportExcel = () => {
    window.open(`${client.defaults.baseURL}/participants/${id}/export?token=${localStorage.getItem('accred_token')}`, '_blank');
  };

  const exportResultsExcel = () => {
    window.open(`${client.defaults.baseURL}/results/participant/${id}/export?token=${localStorage.getItem('accred_token')}`, '_blank');
  };

  if (!p) return <div className="card">جارٍ التحميل...</div>;

  const canShowBadgeButton = p.status === 'approved' || p.status === 'printed' || canPreviewBadge;

  return (
    <div>
      <button className="btn btn-outline no-print" style={{ marginBottom: 14 }} onClick={() => navigate(-1)}>← رجوع</button>

      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
            <div
              style={{ position: 'relative', cursor: canEdit ? 'pointer' : 'default' }}
              onClick={() => canEdit && quickPhotoRef.current?.click()}
              title={canEdit ? 'اضغط لتغيير الصورة' : ''}
            >
              <Avatar src={p.photo_path} name={p.full_name_ar} size={92} color={p.color_code} />
              {canEdit && (
                <div style={{
                  position: 'absolute', bottom: -2, left: -2, width: 26, height: 26, borderRadius: '50%',
                  background: 'var(--signal)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, border: '2px solid #fff'
                }}>{uploadingPhoto ? '…' : '📷'}</div>
              )}
              {canEdit && (
                <input
                  ref={quickPhotoRef} type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={e => quickPhotoUpload(e.target.files[0])}
                />
              )}
            </div>
            <div>
              <h2 style={{ margin: '0 0 4px' }}>{p.full_name_ar}</h2>
              {p.full_name_en && <div style={{ color: 'var(--muted)' }}>{p.full_name_en}</div>}
              <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span className="badge" style={{ background: p.color_code }}>{p.category_name_ar}</span>
                <span className={`badge status-${p.status}`}>{statusLabels[p.status]}</span>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {canEdit && !editing && <button className="btn btn-outline" onClick={() => setEditing(true)}>تعديل البيانات</button>}
            {canShowBadgeButton && <button className="btn btn-outline" onClick={() => setShowBadgePreview(true)}>معاينة البطاقة</button>}
            {(p.status === 'approved' || p.status === 'printed') && <button className="btn btn-outline" onClick={downloadBadge}>تحميل البطاقة</button>}
            <button className="btn btn-outline" onClick={exportExcel}>تصدير Excel</button>
            <button className="btn btn-outline" onClick={() => window.print()}>طباعة الصفحة</button>
            {canReview && p.status !== 'approved' && p.status !== 'rejected' && (
              <>
                <button className="btn btn-success" onClick={() => changeStatus('approved')}>اعتماد</button>
                <button className="btn btn-danger" onClick={() => setShowReject(true)}>رفض</button>
              </>
            )}
          </div>
        </div>
        <div style={{ marginTop: 14, fontSize: 13, color: 'var(--muted)' }}>
          الفعالية: {p.event_name} · الجهة: {p.organization_name} · رقم البطاقة: <span className="mono">{p.badge_number || '—'}</span>
        </div>
        {msg && <div className="alert alert-success" style={{ marginTop: 12 }}>{msg}</div>}
        {p.status === 'rejected' && p.rejection_reason && (
          <div className="alert alert-error" style={{ marginTop: 12 }}>سبب الرفض: {p.rejection_reason}</div>
        )}
      </div>

      {showReject && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h4 style={{ marginTop: 0 }}>سبب الرفض</h4>
          <textarea rows="3" style={{ width: '100%' }} value={rejectReason} onChange={e => setRejectReason(e.target.value)} />
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <button className="btn btn-danger" onClick={() => changeStatus('rejected', rejectReason)}>تأكيد الرفض</button>
            <button className="btn btn-outline" onClick={() => setShowReject(false)}>إلغاء</button>
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>البيانات الشخصية</h3>
        {editing ? (
          <form onSubmit={save}>
            <div className="form-grid">
              {fields.map(([key, label, type]) => (
                <div className="form-field" key={key}>
                  <label>{label}</label>
                  {type === 'gender' ? (
                    <select value={form.gender || 'male'} onChange={e => setForm({ ...form, gender: e.target.value })}>
                      <option value="male">ذكر</option>
                      <option value="female">أنثى</option>
                    </select>
                  ) : (
                    <input type={type} value={form[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })} />
                  )}
                </div>
              ))}
              <div className="form-field">
                <label>تحديث الصورة الشخصية</label>
                <input type="file" accept="image/*" onChange={e => setPhoto(e.target.files[0])} />
              </div>
            </div>
            <div className="form-field" style={{ marginTop: 14 }}>
              <label>ملاحظات</label>
              <textarea rows="3" value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn btn-primary">حفظ التعديلات</button>
              <button type="button" className="btn btn-outline" onClick={() => { setEditing(false); setForm(p); }}>إلغاء</button>
            </div>
          </form>
        ) : (
          <div className="grid grid-2">
            {fields.map(([key, label, type]) => (
              <div key={key}>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>{label}</div>
                <div>{type === 'gender' ? (p.gender === 'male' ? 'ذكر' : 'أنثى') : (p[key] || '—')}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {results.length > 0 && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <h3 style={{ margin: 0 }}>سجل النتائج</h3>
            <button className="btn btn-outline no-print" onClick={exportResultsExcel}>تصدير النتائج Excel</button>
          </div>
          <table>
            <thead><tr><th>المسابقة</th><th>المرحلة</th><th>النتيجة</th><th>الترتيب</th><th>الموسم</th></tr></thead>
            <tbody>
              {results.map(r => (
                <tr key={r.id}>
                  <td>{r.discipline_name_ar}</td>
                  <td>{r.stage === 'final' ? 'نهائي' : 'تصفيات'}</td>
                  <td className="mono">{r.score}</td>
                  <td>{r.rank ?? '—'}</td>
                  <td>{r.season_year}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showBadgePreview && <BadgePreviewModal participantId={id} onClose={() => setShowBadgePreview(false)} />}
    </div>
  );
}
